import {useFormik} from 'formik';
import * as Yup from 'yup';
import {CreateClientVersionDTO, ClientVersionStore, clientVersionsApi} from '../../../entities';
import {AxiosError} from 'axios';
import {useQueryClient, useMutation} from '@tanstack/react-query';
import {useNotifications} from '../../../shared';

interface UseAddClientVersionFormProps {
  setOpen: (open: boolean) => void;
}

export const useAddClientVersionForm = ({setOpen}: UseAddClientVersionFormProps) => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();

  const createClientVersionMutation = useMutation({
    mutationFn: clientVersionsApi.createClientVersion,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['client-versions', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Версия успешно создана',
        name: 'createClientVersion',
        theme: 'success',
      });
      setOpen(false);
    },
  });

  const onSubmit = async (values: CreateClientVersionDTO) => {
    try {
      await createClientVersionMutation.mutateAsync(values);
    } catch (error: any) {
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      store: ClientVersionStore.AppleAppStore,
      versionNumber: 1,
      isMandatory: false,
    },
    validationSchema: Yup.object({
      store: Yup.string().oneOf(Object.values(ClientVersionStore), 'Неверный магазин').required('Магазин обязателен'),
      versionNumber: Yup.number().min(1, 'Номер версии должен быть больше 0').required('Номер версии обязателен'),
      isMandatory: Yup.boolean().required(),
    }),
    onSubmit: onSubmit,
  });

  return {
    values: formik.values,
    errors: formik.errors,
    touched: formik.touched,
    handleChange: formik.handleChange,
    handleBlur: formik.handleBlur,
    setFieldValue: formik.setFieldValue,
    resetForm: formik.resetForm,
    submitForm: formik.submitForm,
    isLoading: createClientVersionMutation.isPending,
    submitError: createClientVersionMutation.error
      ? (createClientVersionMutation.error as AxiosError<ApiError>).response?.data
      : undefined,
  };
};
